import { useDeferredValue, useMemo, useState } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { GenerationSaleSubKind } from '@ebalance/proto/bfftypes/v1/plan_submission_pb'
import { useTranslation } from 'react-i18next'

import { protoDateToCivilDate, requireCivilDate } from '~/models/civilDate'
import { MasterCrudTabShell } from '~/pages/AdminOcctoMaster/shell/MasterCrudTabShell'
import { useMasterCrudTab } from '~/pages/AdminOcctoMaster/shell/useMasterCrudTab'
import { CreateGenerationSalesPlanMasterModal } from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/components/CreateGenerationSalesPlanMasterModal'
import { DeleteGenerationSalesPlanMasterDialog } from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/components/DeleteGenerationSalesPlanMasterDialog'
import { EditGenerationSalesPlanMasterModal } from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/components/EditGenerationSalesPlanMasterModal'
import { GenerationSalesPlanMasterTable } from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/components/GenerationSalesPlanMasterTable'
import { PlanCategoryFilter } from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/components/PlanCategoryFilter'
import { useListGenerationSalesMasters } from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/hooks/useListGenerationSalesMasters'
import { generationSalesPlanMasterCsvColumns } from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/models/csvColumns'
import type { GenerationSalesMasterRow } from '~/pages/AdminOcctoMaster/tabs/GenerationSalesPlanMaster/types'
import { ExpiredFilterToggle } from '~/pages/AdminOcctoMaster/tabs/_common/components/ExpiredFilterToggle'
import { MasterFilterToolbar } from '~/pages/AdminOcctoMaster/tabs/_common/components/MasterFilterToolbar'

function filterRows(
  rows: GenerationSalesMasterRow[],
  areaCode: AreaCode,
  planCategory: GenerationSaleSubKind,
  searchText: string,
): GenerationSalesMasterRow[] {
  const trimmed = searchText.trim().toLowerCase()
  return rows.filter((row) => {
    if (areaCode !== AreaCode.UNSPECIFIED && row.areaCode !== areaCode) {
      return false
    }
    // 計画区分フィルタ (AC-03 / AC-04): 通常/FIT トグルで排他的に絞り込む
    // (= 「すべて」相当は無い、常にどちらか一方が選択されている)。
    if (row.planCategory !== planCategory) {
      return false
    }
    if (trimmed === '') {
      return true
    }
    return (
      row.submitterCode.toLowerCase().includes(trimmed) ||
      (row.note?.toLowerCase().includes(trimmed) ?? false)
    )
  })
}

function convertItem(
  item: import('@ebalance/proto/bffuser/v1/occto_master_model_pb').GenerationSalesMasterListItem,
): GenerationSalesMasterRow {
  return {
    id: item.id,
    submitterCode: item.submitterCode,
    areaCode: item.areaCode,
    planCategory: item.planCategory,
    note: item.note,
    codeCount: item.codeCount,
    bgCount: item.bgCount,
    plantCount: item.plantCount,
    updatedBy: item.updatedBy,
    // optimistic lock 用に proto Timestamp で保持。表示時のみ timestampDate で Date 変換。
    updatedAt: item.updatedAt,
    validFrom: requireCivilDate(
      protoDateToCivilDate(item.validFrom),
      `GenerationSalesMasterListItem.validFrom (id=${item.id})`,
    ),
    validTo: protoDateToCivilDate(item.validTo),
  }
}

/**
 * 発電販売計画マスタ タブ。
 * 一覧 + CRUD モーダルを表示する。
 * 旧 GenerationSalesPartnersTab (= 取引先一覧) を計画マスタ CRUD に作り替えたタブ。
 */
export const GenerationSalesPlanMasterTab = (): React.ReactElement => {
  const { t } = useTranslation()
  const { data, isLoading, isError, error } = useListGenerationSalesMasters()

  const [selectedAreaCode, setSelectedAreaCode] = useState<AreaCode>(
    AreaCode.UNSPECIFIED,
  )
  // AC-03: デフォルトは通常電源。
  const [selectedPlanCategory, setSelectedPlanCategory] =
    useState<GenerationSaleSubKind>(GenerationSaleSubKind.NORMAL)
  const [searchText, setSearchText] = useState('')
  const deferredSearchText = useDeferredValue(searchText)

  const rows = useMemo(
    () => (data !== undefined ? data.items.map(convertItem) : []),
    [data],
  )
  const filteredRows = useMemo(
    () =>
      filterRows(
        rows,
        selectedAreaCode,
        selectedPlanCategory,
        deferredSearchText,
      ),
    [rows, selectedAreaCode, selectedPlanCategory, deferredSearchText],
  )

  const subject = t('page:adminOcctoMaster.common.subject.generationSalesPlan')
  const tab = useMasterCrudTab<GenerationSalesMasterRow>({
    data,
    isLoading,
    isError,
    error,
    errorKeyPrefix:
      'page:adminOcctoMaster.generationSalesPlanMaster.serverError',
    filteredRows,
    csv: {
      columns: generationSalesPlanMasterCsvColumns(),
      fileBase: 'generation-sales-plan',
    },
    expiry: {
      getPeriod: (row) => ({
        validFrom: row.validFrom,
        validTo: row.validTo,
      }),
      subject,
    },
  })

  const deleteRow = tab.modalState.kind === 'delete' ? tab.modalState.row : null

  return (
    <MasterCrudTabShell
      createModal={
        // 計画区分はフィルタ選択値から自動設定し disabled 表示 (AC-05)。
        // 条件 mount (閉じたら form ごと破棄): 常時 mount + isOpen 切替だと
        // 保存成功時の reset() 直後に mount 中の field array 行入力が RHF の
        // 再 register で直前の値を書き戻し、次回 open で行が残留する (#6072、
        // 詳細は DemandProcurementPlanMaster/index.tsx の同コメント)。
        tab.modalState.kind === 'create' ? (
          <CreateGenerationSalesPlanMasterModal
            isOpen
            onClose={tab.closeModal}
            planCategory={selectedPlanCategory}
          />
        ) : null
      }
      csvExportDisabledReason={tab.csvExportDisabledReason}
      deleteDialog={
        deleteRow !== null ? (
          <DeleteGenerationSalesPlanMasterDialog
            row={deleteRow}
            errorMessage={tab.deleteError?.message}
            isOpen={tab.modalState.kind === 'delete'}
            onClose={tab.closeModal}
            onError={(message) => {
              tab.setDeleteError({ message, retriable: true })
            }}
          />
        ) : null
      }
      editModal={
        tab.modalState.kind === 'edit' ? (
          <EditGenerationSalesPlanMasterModal
            row={tab.modalState.row}
            isOpen
            onClose={tab.closeModal}
          />
        ) : null
      }
      filterToolbar={
        <MasterFilterToolbar
          afterArea={
            // 計画区分フィルタ (通常電源 / FIT 特例電源ラジオ、 AC-03 / AC-04)。
            // 本タブ専用 (需要調達計画・需要抑制計画タブは対象外) だが、 design の
            // `.ecl-toolbar` は エリアの直右に並べるため別行にはしない。
            <PlanCategoryFilter
              onChange={setSelectedPlanCategory}
              value={selectedPlanCategory}
            />
          }
          onSearchTextChange={setSearchText}
          onSelectArea={setSelectedAreaCode}
          searchPlaceholder={t(
            'page:adminOcctoMaster.generationSalesPlanMaster.toolbar.searchPlaceholder',
          )}
          searchText={searchText}
          selectedAreaCode={selectedAreaCode}
          trailing={
            <ExpiredFilterToggle
              expiredCount={tab.expiredCount}
              includeExpired={tab.includeExpired}
              onChange={tab.setIncludeExpired}
              subject={subject}
            />
          }
        />
      }
      onAddIndividual={tab.openCreate}
      onCsvExport={tab.csvExportEnabled ? tab.handleCsvExport : undefined}
      showInitialLoading={tab.showInitialLoading}
      table={
        <GenerationSalesPlanMasterTable
          emptyLabel={tab.emptyLabel}
          errorLabel={tab.errorLabel}
          isLoading={isLoading}
          onDelete={tab.openDelete}
          onEdit={tab.openEdit}
          rows={tab.visibleRows}
          today={tab.today}
        />
      }
    />
  )
}
